import { named, withDependencies } from '@wix/thunderbolt-ioc'
import type { ComponentsMeasureEditorConfig, IFiveGridLineMeasures } from './types'
import {
	BrowserWindow,
	BrowserWindowSymbol,
	EditorFeatureConfigSymbol,
	IStructureAPI,
	Structure,
	ViewerManagerStructureAPISymbol,
} from '@wix/thunderbolt-symbols'
import type { ViewerAPI, SiteMeasureMap } from 'thunderbolt-viewer-manager-types'
import { name } from './symbols'
import { getFullId } from '@wix/thunderbolt-commons'
import type { IMeasuresApi } from '@wix/thunderbolt-becky-types'

const fiveGridLineMeasuresFactory = (
	structureViewerApi: ViewerAPI['structure'],
	window: NonNullable<BrowserWindow>,
	config: ComponentsMeasureEditorConfig,
	structureStoreAPI: IStructureAPI
): IFiveGridLineMeasures => {
	const getRotatedCompMeasures = (
		compId: string,
		compElement: HTMLElement,
		measuresApi: IMeasuresApi,
		measureMap: SiteMeasureMap
	) => {
		const compStyle = window.getComputedStyle(compElement)
		const left = parseInt(compStyle.left, 10)

		const wrapperId = `${getFullId(compId)}-rotated-wrapper`
		const wrapper = window.document.querySelector(`[data-mesh-id="${wrapperId}"]`)! as HTMLElement

		const top = measuresApi.getMeasureFromRectRelativeToParent({
			element: wrapper,
			compId,
			measure: 'top',
			offsetValue: measuresApi.getCompHeight(compElement, compId),
			forceIsRotated: true,
			measureMap,
		})

		const compLayout = structureViewerApi.getStructure(compId)?.layout
		const structureY = compLayout?.y || 0
		const structureX = compLayout?.x || 0

		return {
			top: Math.abs(structureY - top) <= 0.5 ? structureY : top,
			left: Math.abs(structureX - left) <= 0.5 ? structureX : left,
		}
	}

	const getMeasuresForFiveGridLine: IFiveGridLineMeasures['getMeasuresForFiveGridLine'] = (
		compId,
		measureMap,
		measuresApi
	) => {
		const compData = config.componentsData[getFullId(compId)]
		const element = measuresApi.getCompElement(compId)!

		return compData.isRotated
			? getRotatedCompMeasures(compId, element, measuresApi, measureMap)
			: {
					top: measuresApi.getCompTop(element, compId, measureMap),
					left: measuresApi.getCompLeft(element, compId, measureMap),
			  }
	}

	const isFiveGridLineComponent: IFiveGridLineMeasures['isFiveGridLineComponent'] = (compId) => {
		const compType = structureStoreAPI.get(compId)?.componentType
		return compType === 'FiveGridLine'
	}

	return {
		isFiveGridLineComponent,
		getMeasuresForFiveGridLine,
	}
}

export const FiveGridLineMeasures = withDependencies(
	[ViewerManagerStructureAPISymbol, BrowserWindowSymbol, named(EditorFeatureConfigSymbol, name), Structure],
	fiveGridLineMeasuresFactory
)
